import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { yearsToRetirementFromAge as retirementYearsForAge } from '../../utils/monthlyProjection';
import { motion } from 'framer-motion';
import {
	Wallet,
	Building2,
	Target,
	TrendingUp,
	Zap,
	Trophy,
	Users,
	Info,
	PiggyBank,
	ArrowRightLeft,
	Receipt
} from 'lucide-react';
import { Button } from '../ui/button';
import { PageHeader } from '../ui/page-header';
import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import TarjetaTraspaso from '../Traspasos/TarjetaTraspaso';
import { traspasosVivosDelPlan } from '../../utils/traspasos/tarjetasDeTraspaso';
import TransferInfoCard from './TransferInfoCard';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import ResponsiveTooltip from '../ui/responsive-tooltip';

import { ArcaContext } from '../../context/ArcaContext.context';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import useArcaPlanReturns from '../../hooks/useArcaPlanReturns';
import { employmentPlanCatalogSlug } from '../../constants/employmentPlanDocs';

// APIs
import getAdhesionDetails from '../../api/users/getAdhesionDetails';
import getCalculatorsUserAportations, {
	PaginatedMonthContributions,
	PaginationParams
} from '../../api/calculators/getCalculatorsUserAportations';
import getMyPensionPlanTransfers from '../../api/pension-plan-transfers/getMyTransfers';
import getUserCostBasis, {
	UserCostBasisData,
	shouldApplyExternalCostBasis
} from '../../api/calculators/getUserCostBasis';
import getMyRanking, { RankingSliceDto } from '../../api/getMyRanking';
import getOnboardingStatusAnonymous from '../../api/companies/getOnboardingStatusAnonymous';
import getCurrentUser from '../../api/users/getCurrentUser';
import getUserEppConfigForMode from '../../api/users/getUserEppConfigForMode';
import getCompanyInfoEppConfig from '../../api/companies/getCompanyInfoEppConfig';
import getIndividualPensionPlan from '../../api/users/getIndividualPensionPlan';
import { IndividualPensionPlan } from '../../api/users/interfaces/IndividualPensionPlan';
import updatePeriodicalContribution from '../../api/users/updatePeriodicalContribution';
import { analytics } from '../../services/analytics';
import { shouldFireContributionUpdated } from '../../utils/shouldFireContributionUpdated';
import calculateMaxAportationsInfo, {
	MaxAportationsInfoOutput
} from '../../api/companies/calculateMaxAportationsInfo';
import { GenericPaginationOutput } from '../../api/generic-dtos/genericPaginationOutput';
import { UserAdhesionDetailsI } from '../../api/users/interfaces/UserAdhesionDetailsI';
import { CompanyEppConfigI } from '../../api/companies/interfaces/CompanyEppConfig';
import { CompanyWithEppConfigI } from '../../api/companies/interfaces/CompanyInfo';
import {
	classifyContribution,
	ContributionCategoryEnum,
	sumRescates
} from '../../utils/contributionClassification';
import { EmployeeContributionTypeEnum } from '../../types/enums';

// Utils
import { calculatePensionPlanMetrics, UserType } from '../../utils/pensionPlanCalculations';
import { fmtEur, fmtNum, formatValueDate } from '../../utils/formatArcaNumbers';
import { useToast } from '../ui/use-toast';
import { resolveEmployerContribution } from '../../utils/resolveEmployerContribution';
import { calculateMinEmployeeContribution } from '../../utils/calculateMinEmployeeContribution';
import {
	percentageToMonthlyEur,
	monthlyEurToPercentage,
	snapPercentageToGrid,
	floorPercentageToGrid,
	ceilPercentageToGrid
} from '../../utils/contributionPercentage';
import { getPlanProductCodes } from '../../utils/getPlanProductCodes';

// V2 Components
import { MetricCard } from 'components/ui/metric-card';
import RankingRowV2 from './RankingRowV2';
import ProjectionSimulatorV2 from './ProjectionSimulatorV2';
import TeamAdoptionV2 from './TeamAdoptionV2';
import ContributionsEvolutionChartV2, {
	MonthlyContributionData
} from './ContributionsEvolutionChartV2';
import EducationalTipV2, { TipData } from './EducationalTipV2';
import EmployeeInviteModalV2 from './EmployeeInviteModalV2';
import ConfigureContributionWrapper from './ConfigureContributionWrapper';
import ExternalPlanCTAV2 from './ExternalPlanCTAV2';
import IndividualPensionPlanFlowV2 from './IndividualPensionPlanFlowV2';

import PensionPlanTransferJourneyV2 from '../PensionPlanTransferJourneyV2';
import RescueRequestDialogV2 from '../RescueRequestDialogV2';
import { reasonStopsContributions } from '../../utils/rescueDocsCatalog';
import { esExEmpleado } from '../../utils/esExEmpleado';
import useWithdrawalRequests from '../../hooks/useWithdrawalRequests';
import useRescuesEnabled from '../../hooks/useRescuesEnabled';
import useSeccionTraspasosEnabled from '../../hooks/useSeccionTraspasosEnabled';
import RescueRentaSimulatorV2 from './RescueRentaSimulatorV2';
import PendingPersonalPlanCardV2 from './PendingPersonalPlanCardV2';
import RescueTaxSimulatorV2 from '../SimulatorsV2/RescueTaxSimulatorV2';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

// Educational tips - built with translate for i18n
const buildTips = (translate: (key: string) => string) => ({
	TAX_TIP: {
		icon: Receipt,
		title: translate('benefit.tips.taxTitle'),
		text: translate('benefit.tips.taxText'),
		cta: translate('benefit.tips.taxCta'),
		action: 'fiscal'
	} as TipData & { action: string },
	COMPANY_MATCH_TIP: {
		icon: Building2,
		title: translate('benefit.tips.companyMatchTitle'),
		text: translate('benefit.tips.companyMatchText'),
		cta: translate('benefit.tips.companyMatchCta'),
		action: 'increase'
	} as TipData & { action: string },
	COMPOUND_TIP: {
		icon: TrendingUp,
		title: translate('benefit.tips.compoundTitle'),
		text: translate('benefit.tips.compoundText'),
		cta: translate('benefit.tips.compoundCta'),
		action: 'simulate'
	} as TipData & { action: string }
});

interface MiBeneficioV2Props {
	userName: string;
	/** Edad del empleado (de su fecha de nacimiento): la proyección va hasta los 67. */
	currentAge?: number;
	isIndividual?: boolean;
	companyName: string;
	onNavigate?: (route: string) => void;
	/** Empleado B2B con su plan individual EN PROCESO (alta del funnel viva). */
	hasPendingPersonalPlan?: boolean;
	/** Ya confirmó el plan del alta en proceso. */
	pendingPersonalPlanLocked?: boolean;
	/** Lleva a la vista del plan individual en proceso. */
	onOpenPendingPersonalPlan?: () => void;
	/**
	 * Euros que suma el enlace del correo (Arca-Team/product#3). Lo entrega el Dashboard: con
	 * él se abre el modal de «Configurar mi aportación» en `current + amount`.
	 */
	contributionPresetDelta?: number | null;
	/** El modal ya se abrió con el preset: el Dashboard lo olvida para no reabrirlo. */
	onContributionPresetConsumed?: () => void;
}

const noop = () => undefined;

const MiBeneficioV2: React.FC<MiBeneficioV2Props> = ({
	userName,
	currentAge,
	isIndividual = false,
	companyName,
	onNavigate,
	hasPendingPersonalPlan = false,
	pendingPersonalPlanLocked = false,
	onOpenPendingPersonalPlan = noop,
	contributionPresetDelta = null,
	onContributionPresetConsumed = noop
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, companyId, userId } = useContext(ArcaContext);
	const { toast } = useToast();

	// State
	const [resolvedCompanyName, setResolvedCompanyName] = useState(companyName);
	const [loading, setLoading] = useState(true);
	const [adhesionDetails, setAdhesionDetails] = useState<UserAdhesionDetailsI[]>([]);
	// CPSIs de ESTE plan (empleo) para scopear aportaciones/base de coste por plan
	// (multi-cuenta). Vacío hasta que llegan las adhesiones → el backend lee legacy.
	const planProductCodes = useMemo(() => getPlanProductCodes(adhesionDetails), [adhesionDetails]);
	const [userTransfers, setUserTransfers] = useState<any[]>([]);

	const [contributions, setContributions] =
		useState<GenericPaginationOutput<PaginatedMonthContributions>>();
	// Base de coste de traspasos (back). El flag multi-plan viaja dentro del propio
	// cost-basis (`hasIndividualPlanSelected`), decidido por el backend.
	const [costBasis, setCostBasis] = useState<UserCostBasisData | null>(null);

	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [showConfigDialog, setShowConfigDialog] = useState(false);
	// Importe del correo para ESTA apertura del modal. Se olvida al cerrarlo: abrirlo después a
	// mano es el modal de siempre.
	const [configPresetDelta, setConfigPresetDelta] = useState<number | undefined>(undefined);
	useEffect(() => {
		if (!contributionPresetDelta) return;
		setConfigPresetDelta(contributionPresetDelta);
		setShowConfigDialog(true);
		onContributionPresetConsumed();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contributionPresetDelta]);
	const [showInviteDialog, setShowInviteDialog] = useState(false);
	const [showTransferDialog, setShowTransferDialog] = useState(false);
	const [showExternalPlanFlow, setShowExternalPlanFlow] = useState(false);
	// Declaración de plan externo de ESTE año (el backend filtra por año): decide si la
	// tarjeta invita a registrar o enseña lo declarado con opción de editarlo.
	const [individualPlan, setIndividualPlan] = useState<IndividualPensionPlan | null>(null);
	const hasDeclaredExternalPlan = !!(individualPlan?.hasIndividual && individualPlan.active);
	const [savingContribution, setSavingContribution] = useState(false);
	const [showCalculatorModal, setShowCalculatorModal] = useState(false);
	const [showRescueDialog, setShowRescueDialog] = useState(false);
	// UX SCAFFOLD (Notion "Definimos MVP rescates") — email, no viene de ningún
	// estado "jubilado/beneficiario" real (no existe como concepto en backend).
	const [userEmail, setUserEmail] = useState('');
	// Motivo de la última solicitud de rescate de ESTE usuario (cualquiera,
	// no solo Ricardo) — si implica dejar de aportar (ver
	// `reasonStopsContributions`), pivota a la vista de beneficiario/a.
	// Estado de rescate de ESTE plan, desde backend. Sólo una solicitud
	// CONFIRMADA por un motivo que corta aportaciones pivota la vista.
	const {
		userEppConfigId,
		activeStoppingReason,
		annuityState,
		pendingAnnuity,
		refresh: refreshWithdrawals
	} = useWithdrawalRequests(token, 'EMPLOYMENT');
	/*
	 * Los traspasos que se enseñan aquí: los de ESTE plan de empleo y todavía vivos.
	 *
	 * `my-transfers` no acepta modo y devuelve todos los del usuario, así que se acota por
	 * `userEppConfigId` —el plan al que pertenece el traspaso—. Un traspaso de plan individual
	 * lo trae a `null` y se queda fuera, que es lo que toca en una pantalla del plan de empleo.
	 *
	 * «Vivo» es lo mismo que para comparsa: los cerrados y los completados viven en Movimientos.
	 */
	// Detrás de su flag mientras se prueba en PROD con unas pocas personas (chirigota#586).
	const { enabled: seccionTraspasos } = useSeccionTraspasosEnabled(
		token,
		companyId ?? null,
		userId
	);
	const traspasosDeEstePlan = seccionTraspasos
		? traspasosVivosDelPlan(userTransfers as PensionPlanTransfer[], userEppConfigId)
		: [];
	// Puerta de rescates: cerrada por defecto mientras no sepamos por Caser
	// quién tiene algo rescatable.
	const { enabled: rescuesEnabled } = useRescuesEnabled(token, companyId ?? null, userId);
	const activeRescueReason = activeStoppingReason;
	// Pivota a la vista de beneficiario sólo si el flag está abierto Y hay una
	// solicitud confirmada por un motivo que corta aportaciones.
	const isRetiredBeneficiary = rescuesEnabled && reasonStopsContributions(activeRescueReason);
	// product#87: la empresa le dio de baja. Su plan sigue a su nombre, pero ya no puede
	// aportar ni le aplica nada de la empresa. Si además rescata, manda la vista de rescate.
	const [exEmpleado, setExEmpleado] = useState(false);
	const vistaExEmpleado = exEmpleado && !isRetiredBeneficiary;

	// Real data from APIs
	const emptySlice: RankingSliceDto = {
		position: 0,
		value: 0,
		deltaToNext: 0,
		positionsToGain: 0,
		percentile: 0
	};
	const [rankings, setRankings] = useState<{
		global: RankingSliceDto;
		company: RankingSliceDto;
		internal: RankingSliceDto;
	}>({ global: emptySlice, company: emptySlice, internal: emptySlice });
	const [teamAdoption, setTeamAdoption] = useState({ adopted: 0, total: 0, invitesSent: 0 });
	const [monthlyCompany, setMonthlyCompany] = useState(0);
	const [monthlyOwn, setMonthlyOwn] = useState(0);
	const [maxMonthly, setMaxMonthly] = useState(0);
	// Aportación recomendada (€/mes). Es la base del máximo del slider en % del hero,
	// igual que en el modal: el tope legal absoluto en % del salario da rangos enormes
	// para sueldos bajos (p. ej. 56%), que hacen el slider inmanejable.
	const [recommendedMonthly, setRecommendedMonthly] = useState(0);
	const [employeeContributionType, setEmployeeContributionType] = useState<string>('');
	const [grossAnnualSalary, setGrossAnnualSalary] = useState(0);
	const [rawPercentageContribution, setRawPercentageContribution] = useState(0);
	const [eppConfig, setEppConfig] = useState<CompanyEppConfigI | null>(null);
	// Rentabilidad de cada plan de Arca, de comparsa (`/plan-returns/arca-plans`, chirigota#576):
	// la del plan de empleo de su empresa alimenta la proyección. `undefined` mientras llega.
	// Con su edad, los de ciclo de vida llegan con sus tramos (chirigota#592).
	const arcaPlanReturns = useArcaPlanReturns(token, retirementYearsForAge(currentAge));

	// Available years for chart — derived from actual contribution data
	const currentYear = new Date().getFullYear();
	const [contributionYears, setContributionYears] = useState<number[]>([currentYear]);
	const availableYears = contributionYears;

	// Fetch adhesion details + rankings + team adoption + user config
	const fetchData = useCallback(
		async (silent = false) => {
			if (!token) return;
			if (!silent) setLoading(true);
			try {
				const [
					adhesionRes,
					transfersRes,
					rankingRes,
					onboardingRes,
					userRes,
					eppConfigRes,
					companyConfigRes,
					maxAportationsRes,
					individualPlanRes
				] = await Promise.all([
					// Vista de EMPLEO: pedimos explícitamente el plan de empleo.
					// 'EMPLOYMENT' garantiza que usuarios mixtos (B2B con rol
					// 'individual') NO reciban datos del plan individual mezclados
					// con los del empleo (Fase 3, comparsa PR #590).
					getAdhesionDetails(token, 'EMPLOYMENT'),
					getMyPensionPlanTransfers(token),
					getMyRanking(token).catch(() => null),
					getOnboardingStatusAnonymous(token).catch(() => null),
					getCurrentUser(token).catch(() => null),
					// Config (amount/día/tipo) de ESTE plan por modo — reemplaza la lectura
					// de `current-user.userEppConfig`, que resuelve "el plan por defecto"
					// (más antiguo+activo) y puede NO ser el plan de empleo/autónomo en
					// multi-cuenta (Fase 4, épica multi-cuenta).
					getUserEppConfigForMode(token, 'EMPLOYMENT').catch(() => null),
					companyId
						? getCompanyInfoEppConfig(token, companyId).catch(() => null)
						: Promise.resolve(null),
					// Fase 4 multi-cuenta (fix 21-jul): sin `mode`, este endpoint resolvía
					// "la" config por defecto (más antigua+activa), que en un usuario con
					// ≥2 filas podía ser el plan INDIVIDUAL en vez del de EMPLEO/autónomo
					// que esta vista muestra.
					calculateMaxAportationsInfo(token, 'EMPLOYMENT').catch(() => null),
					// Va en el mismo lote paralelo: no añade latencia. Se pide aparte en vez
					// de derivarlo de `individualExtraAportations` porque ese importe también
					// incluye lo que reporta Indexa, y etiquetarlo como "declarado" sería
					// mentir a quien no ha declarado nada a mano.
					getIndividualPensionPlan(token).catch(() => null)
				]);

				if (adhesionRes.success && Array.isArray(adhesionRes.data)) {
					setAdhesionDetails(adhesionRes.data);
				}

				if (transfersRes.success && transfersRes.data) {
					setUserTransfers(transfersRes.data);
				}

				// Solo se pisa el estado si la respuesta es buena: la llamada lleva
				// `.catch(() => null)`, así que un 500 pasajero borraría lo declarado y el
				// botón volvería a "Registrar" justo después de que el usuario declarase.
				if (individualPlanRes?.success) {
					setIndividualPlan(individualPlanRes.data ?? null);
				}

				// Rankings from API
				if (rankingRes?.success && rankingRes.data?.rankings) {
					const r = rankingRes.data.rankings;
					setRankings({
						global: r.employeeGlobal,
						company: r.companyGlobal,
						internal: r.employeeCompany
					});
				}

				// Team adoption from onboarding status
				if (onboardingRes?.success && onboardingRes.data) {
					const os = onboardingRes.data;
					setTeamAdoption({
						adopted: os.activeEmployees,
						total: os.totalEmployees,
						invitesSent: 0
					});
				}

				// Monthly contributions from per-employee config
				const fetchedEppConfig = companyConfigRes?.success
					? companyConfigRes.data?.allCompanies?.find(
							(c: CompanyWithEppConfigI) => c.id === companyId
						)?.companyEppConfig ?? null
					: null;
				setEppConfig(fetchedEppConfig);
				const userData = userRes?.success ? userRes.data : null;
				setExEmpleado(esExEmpleado(userData));
				const resolvedEmail = userData?.user?.email || '';
				setUserEmail(resolvedEmail);
				if (resolvedEmail) {
				}
				// Config (amount/tipo/creación) de ESTE plan por modo — sustituye a
				// `userData.userEppConfig`, que resolvía el plan "por defecto" y podía
				// no ser el de empleo/autónomo en multi-cuenta.
				const eppConfigData = eppConfigRes?.data ?? null;

				// Store employee contribution type and salary for display
				if (eppConfigData?.data?.employeeContributionType) {
					setEmployeeContributionType(eppConfigData.data.employeeContributionType);
				}
				if (userData?.user?.grossAnnualSalary) {
					setGrossAnnualSalary(userData.user.grossAnnualSalary);
				}
				if (
					(eppConfigData?.data?.employeeContributionType as string) ===
						EmployeeContributionTypeEnum.PERCENTAGE &&
					eppConfigData?.data?.pensionContribution != null
				) {
					setRawPercentageContribution(Number(eppConfigData.data.pensionContribution));
				}

				// Fallback company name from API when prop is empty
				if (!companyName && companyConfigRes?.success) {
					const apiName = companyConfigRes.data?.allCompanies?.find(
						(c: CompanyWithEppConfigI) => c.id === companyId
					)?.name;
					if (apiName) setResolvedCompanyName(apiName);
				}
				setMonthlyCompany(
					resolveEmployerContribution({
						calculatedEmployerContribution: userData?.calculatedEmployerContribution,
						companyEppConfig: fetchedEppConfig,
						employeeCategory:
							userData?.user?.employeeCategory || eppConfigData?.data?.employeeCategory,
						grossAnnualSalary: userData?.user?.grossAnnualSalary,
						employmentStartDate: userData?.user?.employmentStartDate
					})
				);

				// Determine available years from enrollment date
				if (eppConfigData?.createdAt) {
					const enrollmentYear = new Date(eppConfigData.createdAt).getFullYear();
					const years = [];
					for (let y = enrollmentYear; y <= currentYear; y++) {
						years.push(y);
					}
					setContributionYears(years);
				}

				// Max monthly + own contribution from calculateMaxAportationsInfo API
				if (maxAportationsRes?.success && maxAportationsRes.data) {
					const eData = maxAportationsRes.data as MaxAportationsInfoOutput;
					setMaxMonthly(eData.maxUserContributionEndOfNextPeriod ?? 0);
					setRecommendedMonthly(eData.recommendedConfig?.participantContributionPerPeriod ?? 0);
					const rawOwn = eData.currentConfig?.participantContributionPerPeriod ?? 0;
					const isPercentage =
						(eppConfigData?.data?.employeeContributionType as string) ===
						EmployeeContributionTypeEnum.PERCENTAGE;
					const salary = userData?.user?.grossAnnualSalary ?? 0;
					if (isPercentage && salary > 0) {
						setMonthlyOwn(((rawOwn / 100) * salary) / 12);
					} else if (isPercentage) {
						// PERCENTAGE sin salario: no se puede calcular el euro → 0 (no mostramos
						// el % como si fueran euros).
						setMonthlyOwn(0);
					} else {
						setMonthlyOwn(rawOwn);
					}
				} else if (userData) {
					const rawContribution = Number(eppConfigData?.data?.pensionContribution ?? 0);
					const salary = userData.user?.grossAnnualSalary ?? 0;
					const isPercentage =
						(eppConfigData?.data?.employeeContributionType as string) ===
						EmployeeContributionTypeEnum.PERCENTAGE;
					if (isPercentage && salary > 0) {
						setMonthlyOwn(((rawContribution / 100) * salary) / 12);
					} else if (isPercentage) {
						// PERCENTAGE sin salario: no se puede calcular el euro → 0.
						setMonthlyOwn(0);
					} else {
						setMonthlyOwn(rawContribution);
					}
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				setLoading(false);
			}
		},
		[token, companyId]
	);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleQuickSave = useCallback(
		async (newValue: number) => {
			if (!token) return;
			// El simulador emite el valor en su unidad NATIVA: porcentaje en empresas
			// PERCENTAGE (lo que el backend espera), euros en FIXED. Por eso `newValue`
			// se manda directo SIN conversión → mata el bug de "euros guardados como %".
			// OJO: PERCENTAGE NO depende del salario (el % se persiste tal cual); si exigiéramos
			// salario, sin él caería a euros y guardaría el euro crudo como % (bug de Miguel).
			// Para analytics derivamos el equivalente en € (0 si no hay salario, no se inventa).
			const isPct = employeeContributionType === EmployeeContributionTypeEnum.PERCENTAGE;
			// Última red antes de persistir: el % SIEMPRE en la rejilla. Aunque el slider ya
			// opera en pasos de 0,05, la aritmética de Radix puede devolver 1,3000000000000003
			// y el tope, si viniera crudo, un float de 15 decimales. Lo que se guarda tiene que
			// ser mostrable con 2 decimales, o el % y su euro dejan de cuadrar.
			const valueToSave = isPct ? snapPercentageToGrid(newValue) : newValue;
			const newAmountEur = isPct
				? percentageToMonthlyEur(valueToSave, grossAnnualSalary)
				: valueToSave;
			// Capturamos el importe previo (en €) ANTES de la petición para diferenciar
			// modificación vs primera configuración. Spec Notion: contribution_updated
			// dispara cuando el usuario guarda un cambio en su aportación existente.
			const previousAmount = monthlyOwn;
			setSavingContribution(true);
			try {
				await updatePeriodicalContribution(token, valueToSave, false, 'EMPLOYMENT');
				if (shouldFireContributionUpdated(previousAmount, newAmountEur)) {
					analytics.contributionUpdated({ previousAmount, newAmount: newAmountEur });
				}
				await fetchData(true);
				toast({
					title: translate('benefit.simulator.savedTitle'),
					description: translate('benefit.simulator.savedDescription')
				});
			} catch (error) {
				console.error('Error saving contribution:', error);
				toast({
					variant: 'destructive',
					title: translate('benefit.simulator.saveErrorTitle'),
					description: translate('benefit.simulator.saveErrorDescription')
				});
			} finally {
				setSavingContribution(false);
			}
		},
		[token, fetchData, toast, translate, monthlyOwn, employeeContributionType, grossAnnualSalary]
	);

	// Fetch contributions for chart
	useEffect(() => {
		const fetchContributions = async () => {
			if (!token) return;
			try {
				const params: PaginationParams = {
					page: 1,
					limit: 12,
					year: selectedYear,
					productCodes: planProductCodes
				};
				const res = await getCalculatorsUserAportations(token, params);
				setContributions(res);
			} catch (error) {
				console.error('Error fetching contributions:', error);
			}
		};

		fetchContributions();
	}, [token, selectedYear, planProductCodes]);

	// Corrección del rendimiento de planes traspasados: traemos la base de coste real
	// (back, `user-cost-basis`) y si el usuario tiene plan individual (multi-plan) NO la
	// aplicamos — en mixtos el traspaso individual lleva el company_id del empleador y
	// mezclaría planes. El scope por plan para mixtos queda pendiente de tarea de
	// arquitectura; aquí arreglamos el ~99% (usuarios NO multi-plan).
	useEffect(() => {
		if (!token) return;
		getUserCostBasis(token, planProductCodes)
			.then(setCostBasis)
			.catch((error) => console.error('Error fetching cost basis:', error));
	}, [token, planProductCodes]);

	// Payroll lockout — same logic as EditContributionModalV2
	const isPayrollLocked = useMemo(() => {
		const day = eppConfig?.contributionDay ?? 1;
		const today = new Date();
		let daysUntil = day - today.getDate();
		if (daysUntil < 0) {
			const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
			daysUntil = lastDay - today.getDate() + day;
		}
		return eppConfig !== null && daysUntil <= 4 && daysUntil >= 0;
	}, [eppConfig]);

	// Min employee contribution for hero slider (en euros, para empresas FIXED)
	const minMonthly = useMemo(
		() =>
			calculateMinEmployeeContribution({
				employeeContributionRequired: eppConfig?.employeeContributionRequired,
				employeeContributionMinimum: eppConfig?.employeeContributionMinimum,
				employeeContributionType: employeeContributionType,
				grossAnnualSalary: grossAnnualSalary
			}),
		[eppConfig, employeeContributionType, grossAnnualSalary]
	);

	// Mínimo en % para el slider del hero en empresas PERCENTAGE. Usamos
	// `employeeContributionMinimum` (misma fuente que calculateMinEmployeeContribution),
	// gateado a que la aportación sea obligatoria. Para IDFinance = 1.
	// Number(): employeeContributionMinimum llega como STRING del backend; si se pasa tal
	// cual al slider, Radix lo concatena y la bolita se dispara. Forzamos número.
	// ceil: si el mínimo de la empresa cayera fuera de la rejilla (p. ej. 1,33 %), bajarlo
	// al múltiplo anterior dejaría al partícipe por debajo de su mínimo contractual.
	const percentageMin = useMemo(
		() =>
			eppConfig?.employeeContributionRequired && eppConfig?.employeeContributionMinimum
				? ceilPercentageToGrid(Number(eppConfig.employeeContributionMinimum))
				: 0,
		[eppConfig]
	);

	// Máximo en % del slider del hero. Tiene que dar un rango USABLE y que NUNCA colapse:
	// - El tope legal en euros pasado a % da rangos enormes para sueldos bajos
	//   (1.587€ ≈ 56% a 33k) → slider de ~1.100 pasos, imposible de afinar.
	// - La recomendada es dinámica y puede caer por debajo del mínimo (si ya se ha
	//   aportado mucho) → si la usáramos de tope, max == min → rango cero → la bolita
	//   se dispara al extremo y no se puede mover de 0,05 en 0,05.
	// Solución: techo = recomendada si aporta margen real sobre el mínimo; si no, un span
	// razonable por defecto (mín + 4 pts). Siempre acotado al tope legal y SIEMPRE > mín.
	// TODO(producto): confirmar el tope deseado del slider rápido (el diseño muestra 8 %).
	const HERO_PERCENTAGE_DEFAULT_SPAN = 4;
	// floor a la rejilla: el tope sale de un CÁLCULO (recomendación fiscal / límite legal),
	// no de una elección, así que arrastra decimales infinitos. Si el usuario llega al
	// extremo del slider debe llevarse un múltiplo de 0,05, y hacia ABAJO — quedarse unos
	// céntimos por debajo del techo real es correcto; pasarse de él, nunca.
	const percentageMax = useMemo(() => {
		if (grossAnnualSalary <= 0) return Math.max(percentageMin + 0.05, 5);
		const legalPct = floorPercentageToGrid(monthlyEurToPercentage(maxMonthly, grossAnnualSalary));
		const recommendedPct = floorPercentageToGrid(
			monthlyEurToPercentage(recommendedMonthly, grossAnnualSalary)
		);
		const target =
			recommendedPct > percentageMin
				? recommendedPct
				: percentageMin + HERO_PERCENTAGE_DEFAULT_SPAN;
		// Nunca por encima del tope legal (si éste existe) ni igual/menor que el mínimo.
		const capped = legalPct > 0 ? Math.min(legalPct, target) : target;
		return Math.max(percentageMin + 0.05, capped);
	}, [recommendedMonthly, maxMonthly, grossAnnualSalary, percentageMin]);

	// Calculate metrics. Filtramos transfers a `EMPLEO` para usuarios mixtos
	// (B2B con rol individual) — sin filtro, traspasos individuales contaminarían
	// el rendimiento del plan empleo.
	const metrics = useMemo(() => {
		// Gate multi-plan: el backend marca en el cost-basis si el usuario tiene plan
		// individual REAL (`hasIndividualPlanSelected`); en ese caso NO aplicamos, para no
		// mezclar su traspaso individual con el de empleo. El scope por plan en mixtos queda
		// para la tarea de arquitectura; aquí arreglamos a los usuarios NO multi-plan.
		const applyCostBasis = shouldApplyExternalCostBasis(costBasis);
		return calculatePensionPlanMetrics({
			adhesionDetails,
			userType: UserType.COMPANY,
			transfers: userTransfers,
			planTypology: 'EMPLEO',
			externalTransferCostBasis: applyCostBasis ? costBasis.externalTransferCostBasis : 0,
			totalRescued: applyCostBasis ? costBasis.totalRescued : 0
		});
	}, [adhesionDetails, userTransfers, costBasis]);

	// Transform contributions to chart data
	const chartData: MonthlyContributionData[] = useMemo(() => {
		if (!contributions?.items) return [];

		const monthNames = [
			'Ene',
			'Feb',
			'Mar',
			'Abr',
			'May',
			'Jun',
			'Jul',
			'Ago',
			'Sep',
			'Oct',
			'Nov',
			'Dic'
		];
		const contributionsByMonth = new Map<string, { empresa: number; propia: number }>();

		contributions.items.forEach((item) => {
			let empresa = 0;
			let propia = 0;

			item.contributions.forEach((c) => {
				const category = classifyContribution(c.typeContribution);
				if (category === ContributionCategoryEnum.COMPANY) {
					empresa += c.contributionAmount;
				} else if (category === ContributionCategoryEnum.PARTICIPANT) {
					propia += c.contributionAmount;
				}
			});

			contributionsByMonth.set(item.month, { empresa, propia });
		});

		// Generate cumulative data only for months that have data
		let cumEmpresa = 0;
		let cumPropia = 0;
		const maxMonth = selectedYear === currentYear ? new Date().getMonth() + 1 : 12;
		const result: MonthlyContributionData[] = [];

		for (let i = 0; i < maxMonth; i++) {
			const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
			const data = contributionsByMonth.get(monthKey);

			if (!data && i === maxMonth - 1) break; // skip current month if no data

			cumEmpresa += data?.empresa || 0;
			cumPropia += data?.propia || 0;

			result.push({
				month: monthNames[i],
				empresa: cumEmpresa,
				propia: cumPropia
			});
		}

		return result;
	}, [contributions, selectedYear, currentYear]);

	// Siempre el saldo REAL. Antes, quien pivotaba a esta vista veía un importe
	// fijo sacado del caso de referencia — con el estado saliendo del backend,
	// eso significaba enseñarle a cualquiera el saldo de otra persona.
	const totalSaved = metrics.totalSaved;
	const companyContributions = metrics.sumCompanyContributions / 100;
	const userContributions = metrics.sumUserContributions / 100;
	const performance = metrics.performance;
	const roi = metrics.roi;
	const participations = Number(metrics.actionsProduct?.numParticipations || 0) / 1000000;
	// Mismo cálculo que la card "Rescatado {año}" de ConfigurarBeneficioV2 —
	// sigue siendo por año seleccionado, no histórico acumulado (gap conocido,
	// ver Notion "Definimos MVP rescates", pieza 5).
	const totalRescatado = (contributions?.items || []).reduce(
		(sum, monthData) => sum + Math.abs(sumRescates(monthData.contributions)),
		0
	);

	// Fecha del valor del plan: la de su valor liquidativo, o la de la foto (product#74)
	const lastUpdateDate = useMemo(() => formatValueDate(adhesionDetails[0]), [adhesionDetails]);

	// Plan de empleo contratado por su empresa → su rentabilidad de comparsa, por su nombre en
	// el catálogo. Sin plan asignado o sin datos, null (no se proyecta).
	const employmentSlug = employmentPlanCatalogSlug(eppConfig?.caserPlanSelected);
	const employmentPlanReturn =
		arcaPlanReturns === undefined
			? undefined
			: (employmentSlug && arcaPlanReturns[employmentSlug]) || null;
	// Años hasta los 67 con su edad (0 si ya los tiene); sin edad válida, el simulador no la inventa.
	const yearsToRetirementFromAge =
		currentAge !== undefined && Number.isFinite(currentAge) && currentAge > 0
			? Math.max(0, 67 - Math.round(currentAge))
			: undefined;

	if (loading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
			</div>
		);
	}

	const content = (
		<div className="max-w-6xl mx-auto space-y-8 px-4 pb-4 lg:px-6 lg:pb-6">
			{/* 1. HERO — la cabecera la pone `ui/PageHeader`, puerto de Proa v0.10.0.
			    🔴 Antes el `h1` cambiaba de tipografía según el caso: la jubilada llevaba el
			    canónico y el resto `text-3xl font-bold`, SIN `font-heading`. Dos personas, la
			    misma pantalla, dos tipografías. Ahora es una.
			    Y el aire de arriba solo lo tenía la rama de jubilada (`pt-12 lg:pt-14` condicional
			    en el contenedor); ahora lo tienen las dos, porque lo pone el componente.
			    ⚠️ El contenedor pasa a `px-*`/`pb-*`: con `p-4` el relleno de arriba se sumaría al
			    del componente y la cabecera saltaría de 48/56 px a 64/80.
			    `width="full"` porque el ancho lo sigue poniendo el contenedor (`max-w-6xl`). */}
			<motion.div
				initial={{ opacity: 0, y: -8 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4 }}
			>
				<PageHeader
					width="full"
					eyebrow={
						isRetiredBeneficiary
							? undefined
							: `${translate('admin.home.hello').replace('{name}', '')} 👋`
					}
					title={
						<>
							<span className="text-primary">{userName}</span>, {translate('benefit.hero.youHave')}{' '}
							<span className="text-primary">{fmtEur(totalSaved)}</span>{' '}
							{translate('benefit.hero.saved')}
						</>
					}
					subtitle={
						<>
							{vistaExEmpleado ? (
								// «Tu plan de empleo de <empresa>»: en inglés la empresa va en medio.
								translate('benefit.exEmployee.subtitle')
									.split('{company}')
									.map((trozo, i) => (
										<React.Fragment key={i}>
											{i > 0 && (
												<span className="font-medium text-foreground">{resolvedCompanyName}</span>
											)}
											{trozo}
										</React.Fragment>
									))
							) : (
								<>
									{translate('benefit.hero.yourPlanWith')}{' '}
									<span className="font-medium text-foreground">{resolvedCompanyName}</span>
								</>
							)}
							{lastUpdateDate && (
								<>
									{' · '}
									{translate('benefit.hero.valueAsOf')} {lastUpdateDate}
								</>
							)}
						</>
					}
					action={
						/* Jubilada: la renta mensual ya tiene protagonismo propio justo debajo, así
						   que aquí solo el rescate PUNTUAL, distinto de la renta periódica. */
						isRetiredBeneficiary ? (
							<Button
								className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
								onClick={() => setShowRescueDialog(true)}
							>
								<PiggyBank className="w-4 h-4" />
								{translate('rescue.miBeneficio.requestButton')}
							</Button>
						) : vistaExEmpleado ? undefined : (
							<Button
								variant="outline"
								className="rounded-full gap-2 border-primary text-primary hover:bg-primary/5"
								onClick={() => setShowConfigDialog(true)}
							>
								<Wallet className="w-4 h-4" />
								{translate('benefit.hero.increaseContribution')}
							</Button>
						)
					}
				/>
			</motion.div>

			{/* Ex-empleado (product#87): lo primero que lee es por qué su pantalla ha cambiado. */}
			{vistaExEmpleado && (
				<Alert>
					<Info className="w-4 h-4" />
					<AlertDescription>
						{/* Sin nombre de empresa, «tu empresa» (revisión total, Marisa 01/11). */}
						{translate('benefit.exEmployee.notice').replace(
							'{company}',
							resolvedCompanyName || translate('benefit.exEmployee.yourCompany')
						)}
					</AlertDescription>
				</Alert>
			)}

			{/* Plan individual EN PROCESO: su acceso fuera del selector, justo bajo el saludo. */}
			{hasPendingPersonalPlan && (
				<PendingPersonalPlanCardV2
					translate={translate}
					onOpen={onOpenPendingPersonalPlan}
					isLocked={pendingPersonalPlanLocked}
				/>
			)}

			{/* 2. PROJECTION SIMULATOR — para un beneficiario/jubilado no tiene sentido
			    proyectar aportaciones futuras: ya no aporta, solo rescata. Se sustituye
			    por el slider de renta mensual + educación en esa misma posición. */}
			{isRetiredBeneficiary ? (
				<motion.div
					initial={{ opacity: 0, y: 8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.1 }}
				>
					<RescueRentaSimulatorV2
						userEppConfigId={userEppConfigId}
						annuityState={annuityState}
						pendingAnnuity={pendingAnnuity}
						token={token}
						onSubmitted={() => refreshWithdrawals()}
						availableToRescue={totalSaved}
						email={userEmail}
						currentReason={activeRescueReason}
						seniorityAmount={undefined}
						onOpenCalculator={() => setShowCalculatorModal(true)}
					/>
				</motion.div>
			) : vistaExEmpleado ? null : (
				<ProjectionSimulatorV2
					currentSaved={totalSaved}
					monthlyOwn={monthlyOwn}
					monthlyCompany={monthlyCompany}
					maxMonthly={maxMonthly}
					minMonthly={minMonthly}
					onContributionChange={handleQuickSave}
					onConfigureClick={() => setShowConfigDialog(true)}
					saving={savingContribution}
					translate={translate}
					employeeContributionType={employeeContributionType}
					rawPercentageContribution={rawPercentageContribution}
					percentageMin={percentageMin}
					percentageMax={percentageMax}
					grossAnnualSalary={grossAnnualSalary}
					isPayrollLocked={isPayrollLocked}
					contributionDayDisplay={eppConfig?.contributionDay ?? 1}
					planReturn={employmentPlanReturn}
					yearsToRetirement={yearsToRetirementFromAge}
				/>
			)}

			{isRetiredBeneficiary ? (
				/* Beneficiaria/jubilada: Total ahorrado, Rendimiento y Total
				   rescatado (sin aportación activa, el resto de métricas no aporta
				   nada). Misma tarjeta, mismo borde, misma importancia visual,
				   una junto a la otra. */
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
					<Card className="border border-border shadow-xs h-full">
						<CardContent className="p-5">
							<div className="flex items-start justify-between mb-3">
								<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
									<Wallet className="w-5 h-5 text-primary" />
								</div>
							</div>
							<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
								{translate('benefit.metrics.totalSaved')}
							</p>
							<p className="text-2xl font-bold tracking-tight text-primary">{fmtEur(totalSaved)}</p>
						</CardContent>
					</Card>
					<Card className="border border-border shadow-xs h-full">
						<CardContent className="p-5">
							<div className="flex items-start justify-between mb-3">
								<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
									<TrendingUp className="w-5 h-5 text-primary" />
								</div>
							</div>
							<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
								{translate('benefit.returns.performance')}
							</p>
							<p className="text-2xl font-bold tracking-tight text-primary">
								{performance.percentage >= 0 ? '+' : ''}
								{fmtNum(performance.percentage, 2)}%
							</p>
							<p className="text-xs text-muted-foreground mt-1">
								{performance.amount >= 0 ? '+' : ''}
								{fmtEur(performance.amount)}
							</p>
						</CardContent>
					</Card>
					<Card className="border border-border shadow-xs h-full">
						<CardContent className="p-5">
							<div className="flex items-start justify-between mb-3">
								<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
									<PiggyBank className="w-5 h-5 text-primary" />
								</div>
							</div>
							<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
								{translate('rescue.miBeneficio.totalRescuedLabel').replace(
									'{{year}}',
									String(selectedYear)
								)}
							</p>
							<p className="text-2xl font-bold tracking-tight text-primary">
								{fmtEur(totalRescatado)}
							</p>
						</CardContent>
					</Card>
				</div>
			) : (
				<>
					{/*
					 * 🔴 Traspasos del plan de empleo, bajo las dos tarjetas grandes (decisión de
					 * Elena, 30/09/26). Hasta ahora esta pantalla no enseñaba ninguno: ni aquí, ni
					 * en Movimientos —que solo guarda los completados—, ni en Plan de Empleo. Una
					 * empleada pendiente de SU FIRMA no lo veía en ninguna parte.
					 *
					 * Cuatro decisiones, cada una arreglando un defecto de la primera versión:
					 *
					 * 1. `traspasosDeEstePlan`: `my-transfers` devuelve TODOS los del usuario y
					 *    esta pantalla es del plan de EMPLEO. Sin acotar, a un empleado con plan
					 *    individual le salía aquí su traspaso de BBVA. Todo lo que hay alrededor
					 *    ya pasa `'EMPLOYMENT'` por el mismo motivo.
					 * 2. Solo los VIVOS. Los completados se van a Movimientos, igual que en Inicio.
					 *    Antes se quedaban aquí para siempre con `alwaysShowConfirmed`, que además
					 *    es una prop de depuración —lo dice su propia documentación— y al montarse
					 *    quemaba la marca de «visto»: quien pasaba antes por aquí ya no veía nunca
					 *    la confirmación en Inicio.
					 * 3. `individual-v2` en el envoltorio: `surface-card` SOLO existe dentro de ese
					 *    ámbito (`index.css:611`), y esta rama del componente no lo lleva. Sin él
					 *    las tarjetas salían sin fondo, sin borde y sin sombra.
					 * 4. Sin `ProcessTimeline`. Con `kycStatus="NOT_REQUIRED"` pintaba
					 *    «Verificación de identidad ✓» —un trámite que en empleo no existe— y
					 *    «Tramitando con la gestora» justo cuando falta la firma de la persona.
					 *    Dos afirmaciones falsas. La ficha de datos sí se queda.
					 *
					 * Y NO se pasa `originPreposition`: se queda en «desde X» también para una
					 * consolidación de empleo (decisión de Marisa, 30/09/26). La documentación de
					 * la prop propone «en Tu plan de empleo» porque ahí el dato relevante sería el
					 * destino; queda descartado a propósito, no olvidado.
					 */}
					{traspasosDeEstePlan.length > 0 && (
						<div className="individual-v2 space-y-3">
							{traspasosDeEstePlan.map((transfer) => (
								<TarjetaTraspaso
									key={transfer.id}
									transfer={transfer}
									kycDone
									defaultExpanded={false}
								>
									<TransferInfoCard transfer={transfer} translate={translate} />
								</TarjetaTraspaso>
							))}
						</div>
					)}

					{/* 3. KEY METRICS — el ex-empleado solo ve lo que tiene: ni la empresa ni él aportan ya.
					    Su única tarjeta ocupa la fila entera (revisión total, Marisa 01/11). */}
					<div
						className={
							vistaExEmpleado ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 sm:grid-cols-3 gap-4'
						}
					>
						<MetricCard
							index={0}
							label={translate('benefit.metrics.totalSaved')}
							value={fmtEur(totalSaved)}
							sub={`${participations.toFixed(6)} ${translate('benefit.metrics.participations')}`}
							icon={Wallet}
							tooltip={translate('benefit.metrics.totalSavedTooltip')}
						/>
						{!vistaExEmpleado && (
							<>
								<MetricCard
									index={1}
									label={translate('benefit.metrics.companyContributes')}
									value={fmtEur(companyContributions)}
									sub={`${fmtEur(monthlyCompany)}${translate('benefit.metrics.perMonth')}`}
									icon={Building2}
									tooltip={translate('benefit.metrics.companyContributesTooltip')}
								/>
								<MetricCard
									index={2}
									label={translate('benefit.metrics.yourContributions')}
									value={fmtEur(userContributions)}
									sub={
										employeeContributionType === EmployeeContributionTypeEnum.PERCENTAGE &&
										rawPercentageContribution > 0
											? // El MISMO % que el simulador: en la rejilla y con 2 decimales. Antes se
												// formateaba a 1 decimal sobre el valor crudo, así que un 1,505 % guardado
												// salía aquí como "1,5 %", en el simulador como "1,51 %", y el euro de al
												// lado se calculaba a partir de un tercer número.
												`${fmtNum(snapPercentageToGrid(rawPercentageContribution), 2)}% (${fmtEur(monthlyOwn)}${translate('benefit.metrics.perMonth')})`
											: `${fmtEur(monthlyOwn)}${translate('benefit.metrics.perMonth')}`
									}
									icon={Target}
									tooltip={translate('benefit.metrics.yourContributionsTooltip')}
								/>
							</>
						)}
					</div>

					{/* Returns strip */}
					<motion.div
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.3, delay: 0.18 }}
						className="bg-white rounded-xl border border-gray-200 shadow-xs grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-200"
					>
						<div className="p-5 flex items-center gap-4">
							<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
								<TrendingUp className="w-5 h-5 text-primary" />
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-1.5">
									<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
										{translate('benefit.returns.performance')}
									</p>
									<ResponsiveTooltip
										content={translate('benefit.returns.performanceTooltip')}
										side="top"
									>
										<button type="button" className="inline-flex cursor-help">
											<Info className="w-3.5 h-3.5 text-muted-foreground" />
										</button>
									</ResponsiveTooltip>
								</div>
								<div className="flex items-baseline gap-2 mt-0.5">
									<p className="text-xl font-bold text-primary tracking-tight">
										{performance.percentage >= 0 ? '+' : ''}
										{fmtNum(performance.percentage, 2)}%
									</p>
									<p className="text-sm text-primary/70 font-medium">
										{performance.amount >= 0 ? '+' : ''}
										{fmtEur(performance.amount)}
									</p>
								</div>
							</div>
						</div>
						<div className="p-5 flex items-center gap-4">
							<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
								<Zap className="w-5 h-5 text-primary" />
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-1.5">
									<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
										{translate('benefit.returns.totalROI')}
									</p>
									<ResponsiveTooltip
										content={translate('benefit.returns.totalROITooltip')}
										side="top"
									>
										<button type="button" className="inline-flex cursor-help">
											<Info className="w-3.5 h-3.5 text-muted-foreground" />
										</button>
									</ResponsiveTooltip>
								</div>
								<div className="flex items-baseline gap-2 mt-0.5">
									{metrics.roiIndeterminate ? (
										<ResponsiveTooltip
											content={translate('benefit.returns.roiIndeterminateTooltip')}
											side="top"
										>
											<p className="text-sm font-medium text-muted-foreground cursor-help inline-flex items-center gap-1">
												{translate('benefit.returns.roiIndeterminate')}
												<Info className="w-3.5 h-3.5" />
											</p>
										</ResponsiveTooltip>
									) : (
										<>
											<p className="text-xl font-bold text-primary tracking-tight">
												{fmtNum(roi, 2)}%
											</p>
											<p className="text-sm text-primary/70 font-medium">
												{metrics.totalBenefitInEuros >= 0 ? '+' : ''}
												{fmtEur(metrics.totalBenefitInEuros)}
											</p>
										</>
									)}
								</div>
							</div>
						</div>
					</motion.div>
				</>
			)}

			{/* 4. PLAN ACTIONS (Transfer + External Plan) — sin sentido para una
			    beneficiaria/jubilada: ya no tiene nada que traspasar ni que
			    declarar como "otro plan externo", solo puede rescatar. */}
			{/* Ex-empleado (product#87, Marisa 01/11): tampoco. Su plan está suspendido y el
			    traspaso de chirigota es solo de entrada. */}
			{!isRetiredBeneficiary &&
				!vistaExEmpleado &&
				(userTransfers.length === 0 ? (
					<motion.div
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						className="bg-linear-to-r from-secondary/5 to-primary/5 rounded-xl border border-secondary/20 p-5"
					>
						<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
							<div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
								<PiggyBank className="w-6 h-6 text-secondary" />
							</div>
							<div className="flex-1 min-w-0">
								<h3 className="text-base font-semibold text-gray-900 mb-1">
									{translate('user.externalPlan.ctaTitle')}
								</h3>
								<p className="text-sm text-gray-600">{translate('user.planActions.subtitle')}</p>
							</div>
							{/* 🔴 `flex-wrap`: los dos botones llevan `whitespace-nowrap` (base de `Button`), así
							    que su ancho mínimo es el del texto entero y `flex-1` no puede
							    encogerlos por debajo. A 360 px —el ancho de la mayoría de los
							    Android— «Registrar plan externo» se salía 26 px y arrastraba la
							    página entera. A 390 (iPhone) cabía, por eso la red no lo vio: mide
							    a 390. Con `flex-wrap` se apilan cuando no caben. */}
							<div className="flex flex-wrap gap-2 w-full sm:w-auto">
								<Button
									onClick={() => setShowTransferDialog(true)}
									className="bg-secondary hover:bg-secondary/90 text-white gap-1.5 flex-1 sm:flex-initial"
								>
									<ArrowRightLeft className="w-4 h-4" />
									{translate('user.planActions.transferButton')}
								</Button>
								<Button
									variant="outline"
									onClick={() => setShowExternalPlanFlow(true)}
									className="border-secondary text-secondary hover:bg-secondary/5 gap-1.5 flex-1 sm:flex-initial"
								>
									{/* El botón abre el formulario con lo declarado este año, así que
									    llamarlo "Registrar" mentiría sobre lo que va a pasar. */}
									{hasDeclaredExternalPlan
										? translate('user.planActions.editExternalButton')
										: translate('user.planActions.registerButton')}
								</Button>
							</div>
						</div>
					</motion.div>
				) : (
					<ExternalPlanCTAV2
						hasExternalPlan={hasDeclaredExternalPlan}
						externalPlanAmount={hasDeclaredExternalPlan ? individualPlan!.contributionsMade : 0}
						onConfigureClick={() => setShowExternalPlanFlow(true)}
						translate={translate}
					/>
				))}

			{/* 6. TEAM ADOPTION + REFERRAL — sin sentido para una beneficiaria/jubilada:
			    ya no forma parte del "equipo" que aporta al plan. */}
			{!isRetiredBeneficiary && !vistaExEmpleado && (
				<TeamAdoptionV2
					companyName={resolvedCompanyName}
					adopted={teamAdoption.adopted}
					total={teamAdoption.total}
					invitesSent={teamAdoption.invitesSent}
					onInvite={() => setShowInviteDialog(true)}
					translate={translate}
				/>
			)}

			{/* 7. RANKINGS — comparan aportaciones, irrelevante sin aportación activa. */}
			{!isIndividual && !isRetiredBeneficiary && !vistaExEmpleado && (
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
					<div className="flex items-center gap-2 mb-4">
						<Trophy className="w-5 h-5 text-secondary" />
						<h2 className="text-lg font-semibold">{translate('benefit.rankings.title')}</h2>
					</div>
					<div className="space-y-3">
						<RankingRowV2
							icon={<Users className="w-5 h-5 text-primary" />}
							title={translate('benefit.rankings.global')}
							subtitle={translate('benefit.rankings.globalSubtitle').replace(
								'{percentile}',
								String(Math.round(rankings.global.percentile))
							)}
							position={rankings.global.position}
							percentile={rankings.global.percentile}
							amountToNext={rankings.global.deltaToNext}
							onAction={() => setShowConfigDialog(true)}
							translate={translate}
						/>
						<RankingRowV2
							icon={<Building2 className="w-5 h-5 text-primary" />}
							title={translate('benefit.rankings.companies')}
							subtitle={translate('benefit.rankings.companiesSubtitle').replace(
								'{company}',
								resolvedCompanyName
							)}
							position={rankings.company.position}
							percentile={rankings.company.percentile}
							amountToNext={rankings.company.deltaToNext}
							onAction={() => setShowConfigDialog(true)}
							translate={translate}
						/>
						<RankingRowV2
							icon={<Zap className="w-5 h-5 text-primary" />}
							title={translate('benefit.rankings.internal').replace(
								'{company}',
								resolvedCompanyName
							)}
							subtitle={
								rankings.internal.position === 1
									? translate('benefit.rankings.youAreNumber1')
									: translate('benefit.rankings.internalSubtitle').replace(
											'{percentile}',
											String(Math.round(rankings.internal.percentile))
										)
							}
							position={rankings.internal.position}
							percentile={rankings.internal.percentile}
							amountToNext={rankings.internal.deltaToNext}
							isTop={rankings.internal.position === 1}
							onAction={() => setShowConfigDialog(true)}
							translate={translate}
						/>
					</div>
				</motion.div>
			)}

			{/* 8. EVOLUTION CHART */}
			<ContributionsEvolutionChartV2
				data={chartData}
				year={selectedYear}
				availableYears={availableYears}
				onYearChange={setSelectedYear}
				onViewDetail={() => onNavigate?.('/dashboard/my-contributions')}
				translate={translate}
			/>

			{/* 9. EDUCATIONAL TIP — para beneficiaria/jubilada, cómo pedir el rescate
			    en vez de tips sobre seguir aportando. NO se le dice cuánto tiene
			    rescatable: hoy no hay ningún dato de Caser que lo diga. */}
			<EducationalTipV2
				tips={(() => {
					// Al ex-empleado ningún consejo: todos le invitan a aportar (Marisa, 01/11). Sin
					// consejos, EducationalTipV2 no pinta nada.
					if (vistaExEmpleado) return [];
					if (isRetiredBeneficiary) {
						return [
							{
								icon: Wallet,
								title: translate('rescue.miBeneficio.tipTitle'),
								text: translate('rescue.miBeneficio.tipText'),
								cta: translate('rescue.miBeneficio.tipCta'),
								action: 'rescue'
							} as TipData & { action: string }
						];
					}
					const tips = buildTips(translate);
					return [
						monthlyOwn === 0
							? tips.COMPANY_MATCH_TIP
							: monthlyOwn < maxMonthly
								? tips.TAX_TIP
								: tips.COMPOUND_TIP
					];
				})()}
				onCtaClick={(tip) => {
					if ((tip as TipData & { action: string }).action === 'fiscal') {
						onNavigate?.('/dashboard/simulators?tab=fiscal');
					} else if ((tip as TipData & { action: string }).action === 'simulate') {
						onNavigate?.('/dashboard/simulators?tab=planificador');
					} else if ((tip as TipData & { action: string }).action === 'increase') {
						setShowConfigDialog(true);
					} else if ((tip as TipData & { action: string }).action === 'rescue') {
						setShowCalculatorModal(true);
					}
				}}
			/>

			{/* MODALS */}
			<PensionPlanTransferJourneyV2
				isOpen={showTransferDialog}
				onClose={() => setShowTransferDialog(false)}
			/>
			<EmployeeInviteModalV2
				isOpen={showInviteDialog}
				onClose={() => setShowInviteDialog(false)}
				companyName={resolvedCompanyName}
			/>
			<ConfigureContributionWrapper
				// Tampoco por el enlace del correo: el ex-empleado ya no puede aportar.
				isOpen={showConfigDialog && !vistaExEmpleado}
				onClose={() => {
					setShowConfigDialog(false);
					setConfigPresetDelta(undefined);
				}}
				isIndividual={isIndividual}
				onSaved={fetchData}
				presetDeltaAmount={configPresetDelta}
			/>
			<IndividualPensionPlanFlowV2
				isOpen={showExternalPlanFlow}
				// Se llega pulsando "Registrar plan externo": es una acción explícita, así
				// que quien ya declaró este año abre el formulario con su dato para poder
				// corregirlo, en vez de que el flujo se salte solo.
				mode="edit"
				onClose={() => setShowExternalPlanFlow(false)}
				onComplete={() => {
					setShowExternalPlanFlow(false);
					// El tope que se muestra lo calcula el backend a partir del plan externo
					// declarado: sin recargar, el usuario acaba de declarar y la pantalla le
					// sigue ofreciendo el margen de antes.
					fetchData(true);
				}}
				token={token}
			/>
			<Dialog open={showCalculatorModal} onOpenChange={setShowCalculatorModal}>
				<DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto thin-scrollbar">
					<div className="individual-v2 -m-6 p-6 rounded-lg">
						<DialogHeader>
							<DialogTitle className="font-heading">
								{translate('rescue.miBeneficio.calcDialogTitle')}
							</DialogTitle>
						</DialogHeader>
						<div className="mt-6">
							<RescueTaxSimulatorV2
								initialAvailable={totalSaved}
								cardTitle={translate('rescue.miBeneficio.calcCardTitle')}
							/>
						</div>
					</div>
				</DialogContent>
			</Dialog>
			<RescueRequestDialogV2
				isOpen={showRescueDialog}
				onClose={() => setShowRescueDialog(false)}
				userEppConfigId={userEppConfigId}
				token={token}
				onSubmitted={() => refreshWithdrawals()}
				availableAmount={totalSaved}
				email={userEmail}
				currentReason={activeRescueReason}
				seniorityAmount={undefined}
			/>
		</div>
	);

	// 🔴 El sangrado (`-mx-4 -mt-4`) se aplica SIEMPRE; lo único condicional es el fondo.
	//
	// Hasta el 29/09/26 el envoltorio entero era solo para la jubilada, y eso le daba a la MISMA
	// pantalla dos geometrías según quién entrase: el `-mt-4` cancela el `p-4` del Dashboard, así
	// que ella veía 48 px de aire de cabecera y 16 de margen lateral, y cualquier otra 64 y 32.
	// Nadie lo decidió: salía de por qué rama del `return` cayera cada persona.
	//
	// El fondo de marca "individual" sí es suyo: ocupa la pantalla completa y no solo la columna
	// de contenido, para que no se vea un corte de color con el resto de la página.
	return (
		<div className={`-mx-4 -mt-4 ${isRetiredBeneficiary ? 'individual-v2 min-h-screen' : ''}`}>
			{content}
		</div>
	);
};

export default MiBeneficioV2;
